/**
 * A founder replacing their company's logo.
 *
 * Everything shown before this was DERIVED — pulled out of a pitch deck or
 * lifted from a favicon — and a founder had no way to correct it. `logo_source`
 * now has a `founder` tier that outranks every guess; this is what writes it.
 *
 * The picker is separate from the upload on purpose: cancelling a picker is not
 * an error, and conflating the two produced "upload failed" toasts for someone
 * who simply changed their mind.
 */

import { useState, useCallback } from 'react';
import * as ImagePicker from 'expo-image-picker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { handleError } from '@/lib/errors';

/** Mirrors MAX_LOGO_BYTES in the edge function. Rejected here to save a round trip. */
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export interface SetCompanyLogoInput {
  startupId: string;
  /** Local URI from the picker. */
  uri: string;
  /** The founder telling us their mark is near-white and needs a dark chip. */
  isKnockout?: boolean;
}

/**
 * Read a picked image as base64.
 *
 * Base64 is fine at this size — a logo is capped at 2 MB, three orders of
 * magnitude below the isolate ceiling that an 18.5 MB deck blew through. The
 * rule learned there is "no megabytes of document in a JSON body", not "never
 * base64".
 */
async function readAsBase64(uri: string): Promise<{ base64: string; bytes: number }> {
  const response = await fetch(uri);
  const blob = await response.blob();
  if (blob.size > MAX_LOGO_BYTES) {
    throw new Error('That image is over 2 MB. Please choose a smaller file.');
  }
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Strip the `data:image/png;base64,` prefix.
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  return { base64, bytes: blob.size };
}

/** Open the library and return a local URI, or null when the founder cancels. */
export function usePickLogo() {
  const [isPicking, setIsPicking] = useState(false);

  const pick = useCallback(async (): Promise<string | null> => {
    setIsPicking(true);
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        throw new Error('Permission to access photos was denied.');
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 1, // a logo must not be JPEG-smeared
        allowsEditing: false, // cropping a wordmark loses the wordmark
      });
      // Cancelling is not a failure. Returning null keeps it out of the error path.
      if (result.canceled || !result.assets?.length) return null;
      return result.assets[0].uri;
    } finally {
      setIsPicking(false);
    }
  }, []);

  return { pick, isPicking };
}

export function useSetCompanyLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ startupId, uri, isKnockout }: SetCompanyLogoInput) => {
      const { base64 } = await readAsBase64(uri);

      const { data, error } = await supabase.functions.invoke('set-company-logo', {
        body: { startupId, fileBase64: base64, isKnockout: isKnockout === true },
      });

      // supabase.functions.invoke RESOLVES with { error } rather than throwing,
      // so an unbound result would report success for a rejected upload.
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error ?? 'Could not save the logo.');

      return data as { logoUrl: string; logoSource: 'founder' };
    },
    onSuccess: () => {
      // Every surface reads the logo through the directory embed, so invalidate
      // broadly rather than trying to patch each cache by hand.
      queryClient.invalidateQueries({ queryKey: ['dealflow'] });
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['startup-profile'] });
      queryClient.invalidateQueries({ queryKey: ['company-directory'] });
    },
    onError: (err) => {
      reportUploadFailure(err);
    },
  });
}

function reportUploadFailure(err: unknown) {
  handleError(err, { context: 'useSetCompanyLogo' });
}

/**
 * The founder's own company logo, with its provenance.
 *
 * Reads `company_directory` by `startup_profile_id` — the only table that has
 * ever held a logo. The founder needs the SOURCE as well as the URL: a logo we
 * extracted from their deck should be labelled as a guess, because a founder
 * cannot correct something they were never told was a guess.
 */
export function useMyCompanyLogo(startupId: string | null | undefined) {
  return useQuery({
    queryKey: ['company-directory', 'logo', startupId ?? ''],
    enabled: !!startupId,
    queryFn: async () => {
      if (!startupId) return null;
      const { data, error } = await supabase
        .from('company_directory')
        .select('id, logo_url, logo_source, logo_is_knockout')
        .eq('startup_profile_id', startupId)
        .maybeSingle();
      // Bind the error: a silent failure here renders initials, which looks
      // exactly like "you have no logo" and invites a pointless re-upload.
      if (error) throw error;
      return data;
    },
  });
}
